/**
 * AnomalyTriage — Mini-game: split-panel SOC alert feed + device detail.
 *
 * Props:
 *   step          — minigame step object from level JSON:
 *                   { type, difficulty, score, alerts: [...], onComplete }
 *   onComplete    — () => void — called when all alerts are classified
 *   onNodeFlip    — (nodeId: string) => void — called on wrong classification
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeIn, shake } from '../../utils/motion.config';

const SEVERITY = {
  high:   { dot: 'bg-red-500',    badge: 'bg-red-950/60 text-red-300 border border-red-800/50',    label: 'HIGH' },
  medium: { dot: 'bg-amber-400',  badge: 'bg-amber-950/60 text-amber-300 border border-amber-800/50', label: 'MED' },
  low:    { dot: 'bg-slate-400',  badge: 'bg-slate-800/60 text-slate-400 border border-slate-600/50', label: 'LOW' },
};

function AlertRow({ alert, isSelected, classification, onClick }) {
  const sev = SEVERITY[alert.severity] ?? SEVERITY.low;
  const classInfo = classification
    ? classification.choice === 'threat'
      ? { label: 'THREAT', cls: 'bg-red-900/70 text-red-300 border border-red-700/60' }
      : { label: 'CLEAN',  cls: 'bg-green-900/70 text-green-300 border border-green-700/60' }
    : { label: '?', cls: 'bg-slate-700/60 text-slate-400 border border-slate-500/50' };

  return (
    <motion.button
      layout
      onClick={onClick}
      className={[
        'w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors',
        isSelected
          ? 'bg-indigo-950/70 border-indigo-500/70'
          : 'bg-slate-900/60 border-slate-700/40 hover:border-slate-500/60 hover:bg-slate-800/40',
      ].join(' ')}
    >
      {/* severity dot */}
      <span className={`shrink-0 w-2 h-2 rounded-full ${sev.dot}`} />

      {/* id + device */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-slate-400">{alert.id}</span>
          <span className={`text-[9px] font-mono font-bold px-1 rounded ${sev.badge}`}>{sev.label}</span>
        </div>
        <div className="text-sm font-semibold text-slate-200 truncate">{alert.device}</div>
        <div className="text-[11px] text-slate-400 truncate">{alert.type}</div>
      </div>

      {/* classification badge */}
      <span className={`shrink-0 text-[10px] font-mono font-bold px-2 py-0.5 rounded ${classInfo.cls}`}>
        {classInfo.label}
      </span>
    </motion.button>
  );
}

function DetailPanel({ alert }) {
  if (!alert) {
    return (
      <div className="flex items-center justify-center h-full text-slate-500 text-sm italic">
        Select an alert to inspect device details.
      </div>
    );
  }
  return (
    <motion.div key={alert.id} {...fadeIn} className="flex flex-col gap-4 h-full">
      {/* header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-0.5">{alert.id} · {alert.timestamp}</div>
          <div className="text-lg font-bold text-slate-100">{alert.device}</div>
          <div className="text-xs text-slate-400">{alert.ip}</div>
        </div>
        <span className={`text-[10px] font-mono font-bold px-2 py-1 rounded ${(SEVERITY[alert.severity] ?? SEVERITY.low).badge}`}>
          {(SEVERITY[alert.severity] ?? SEVERITY.low).label}
        </span>
      </div>

      {/* metadata grid */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        {[
          ['Device Type', alert.deviceType],
          ['Protocol',    alert.protocol],
          ['IP Address',  alert.ip],
          ['Traffic',     alert.traffic],
        ].map(([label, value]) => (
          <div key={label} className="bg-slate-800/50 rounded-lg px-3 py-2 border border-slate-700/40">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">{label}</div>
            <div className="text-slate-200 font-mono">{value}</div>
          </div>
        ))}
      </div>

      {/* signal */}
      <div className="bg-slate-800/40 rounded-lg px-3 py-2 border border-slate-700/40">
        <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Signal</div>
        <div className="text-sm text-amber-300/90 font-medium">{alert.signal}</div>
      </div>

      {/* description */}
      <div className="flex-1 bg-slate-900/50 rounded-lg px-3 py-2.5 border border-slate-700/30">
        <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Alert Description</div>
        <div className="text-sm text-slate-300 leading-relaxed">{alert.alertDescription}</div>
      </div>
    </motion.div>
  );
}

function FeedbackToast({ feedback, onDismiss }) {
  return (
    <AnimatePresence>
      {feedback && (
        <motion.div
          key={feedback.id + feedback.choice}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.25 }}
          className={[
            'px-4 py-2.5 rounded-lg border text-sm font-medium flex items-start gap-2',
            feedback.correct
              ? 'bg-green-950/70 border-green-700/60 text-green-300'
              : 'bg-red-950/70 border-red-700/60 text-red-300',
          ].join(' ')}
        >
          <span className="shrink-0 font-bold">{feedback.correct ? '✓' : '✗'}</span>
          <span>{feedback.message}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function CompletionPanel({ score, maxScore, correctCount, totalCount, wrongItems, onComplete, onScoreUpdate, onRetry, isReplay }) {
  const allCorrect = correctCount === totalCount;

  if (!allCorrect) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="flex flex-col items-center gap-5 h-full py-8 px-4"
      >
        <div className="text-5xl text-amber-400">!</div>
        <div className="text-center">
          <div className="text-xl font-bold text-slate-100 mb-1">Triage Error{wrongItems.length > 1 ? 's' : ''}</div>
          <div className="text-sm text-slate-400">
            {totalCount - correctCount} alert{totalCount - correctCount > 1 ? 's' : ''} misclassified — review and try again.
          </div>
        </div>
        <div className="w-full max-w-lg flex flex-col gap-2">
          {wrongItems.map((item) => (
            <div key={item.id} className="flex gap-3 px-3 py-2.5 rounded-lg border border-amber-800/40 bg-amber-950/20 text-sm">
              <span className="shrink-0 font-mono text-amber-400 font-bold">{item.id}</span>
              <span className="text-amber-300/80">{item.explanation}</span>
            </div>
          ))}
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onRetry}
          className="px-8 py-2.5 rounded-lg bg-amber-700 hover:bg-amber-600 text-white font-semibold text-sm transition-colors"
        >
          Try Again
        </motion.button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="flex flex-col items-center justify-center gap-6 h-full py-12"
    >
      <div className="text-5xl text-green-400">✓</div>
      <div className="text-center">
        <div className="text-xl font-bold text-slate-100 mb-1">All alerts classified correctly.</div>
        <div className="text-sm text-slate-400">{correctCount} of {totalCount} correct</div>
      </div>
      {!isReplay && (
        <div className="flex items-center gap-3 px-6 py-3 rounded-xl border border-indigo-700/60 bg-indigo-950/50">
          <span className="text-sm text-indigo-300 font-semibold">Defense Score</span>
          <span className="text-2xl font-bold text-indigo-200 font-mono">+{score}</span>
          <span className="text-sm text-indigo-500">/ {maxScore} pts</span>
        </div>
      )}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => {
          onScoreUpdate && onScoreUpdate(score);
          onComplete && onComplete();
        }}
        className="px-8 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm transition-colors"
      >
        Continue
      </motion.button>
    </motion.div>
  );
}

function HelpModal({ onClose }) {
  return (
    <AnimatePresence>
      <motion.div
        key="help-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          key="help-panel"
          initial={{ opacity: 0, scale: 0.95, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 8 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md mx-4 bg-gray-950 border border-indigo-700/50 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-indigo-900/50 bg-indigo-950/60">
            <div className="flex items-center gap-2.5">
              <span className="text-indigo-300 font-bold text-base">?</span>
              <span className="text-sm font-semibold text-indigo-200 tracking-wide">How to Play — Anomaly Triage</span>
            </div>
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-300 text-lg leading-none transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-4 flex flex-col gap-4 text-sm text-slate-300 leading-relaxed">
            <p>
              The SOC alert feed has flagged suspicious activity on the control network.
              Your job is to classify each alert — decide whether it is a <span className="text-red-300 font-semibold">genuine threat</span> or a <span className="text-green-300 font-semibold">false positive</span>.
            </p>

            <ol className="flex flex-col gap-3 list-none">
              {[
                ['Select an alert', 'Click any alert in the left-hand feed to load its device details on the right.'],
                ['Review the evidence', 'Read the device type, protocol, traffic level, and alert description. Check whether the signal is consistent with normal baseline behaviour.'],
                ['Classify it', 'Click FLAG AS THREAT if the signal indicates malicious or unauthorised activity. Click MARK CLEAN if it is a false positive.'],
                ['Repeat for all alerts', 'Every alert must be classified before you can proceed. Decisions are irreversible.'],
              ].map(([title, desc], i) => (
                <li key={i} className="flex gap-3">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-indigo-900/80 border border-indigo-600/60 text-indigo-300 text-[11px] font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <div>
                    <div className="font-semibold text-slate-100 mb-0.5">{title}</div>
                    <div className="text-slate-400 text-xs">{desc}</div>
                  </div>
                </li>
              ))}
            </ol>

            <div className="px-3 py-2.5 rounded-lg border border-amber-800/40 bg-amber-950/30 text-amber-300/90 text-xs">
              <span className="font-semibold">Remember:</span> In OT environments, a false positive carries real operational cost — flagging a clean device takes it offline just as effectively as a real incident.
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function AnomalyTriage({ step, onComplete, onScoreUpdate, onNodeFlip, isReplay }) {
  const alerts = step?.alerts ?? [];
  const maxScore = step?.score ?? 30;
  const ptsPerAlert = alerts.length > 0 ? Math.round(maxScore / alerts.length) : 0;

  const [selectedId, setSelectedId] = useState(alerts[0]?.id ?? null);
  const [classifications, setClassifications] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [shakeTrigger, setShakeTrigger] = useState(0);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const selected = alerts.find((a) => a.id === selectedId);
  const classifiedCount = Object.keys(classifications).length;
  const totalAlerts = alerts.length;

  const classify = (choice) => {
    if (!selected || classifications[selected.id]) return;

    const correct = choice === selected.correctClassification;
    const newClassifications = { ...classifications, [selected.id]: { choice, correct } };
    setClassifications(newClassifications);

    if (correct) {
      setScore((s) => s + ptsPerAlert);
      setFeedback({ id: selected.id, choice, correct: true, message: selected.correctFeedback });
    } else {
      setFeedback({ id: selected.id, choice, correct: false, message: selected.wrongFeedback });
      setShakeTrigger((n) => n + 1);
      onNodeFlip?.('ot');
    }

    const newClassifiedCount = classifiedCount + 1;
    if (newClassifiedCount >= totalAlerts) {
      setTimeout(() => setDone(true), 1800);
    } else {
      // Advance to next unclassified alert
      const nextAlert = alerts.find((a) => a.id !== selected.id && !newClassifications[a.id]);
      if (nextAlert) setTimeout(() => setSelectedId(nextAlert.id), 900);
    }
  };

  const correctCount = Object.values(classifications).filter((c) => c.correct).length;
  const isClassified = selected && !!classifications[selected.id];

  const wrongItems = Object.entries(classifications)
    .filter(([, c]) => !c.correct)
    .map(([id]) => {
      const alert = alerts.find((a) => a.id === id);
      return { id, explanation: alert?.wrongFeedback ?? 'Review this alert.' };
    });

  const handleRetry = () => {
    setClassifications({});
    setScore(0);
    setDone(false);
    setSelectedId(alerts[0]?.id ?? null);
    setFeedback(null);
    setShakeTrigger(0);
  };

  if (done) {
    return (
      <CompletionPanel
        score={score}
        maxScore={maxScore}
        correctCount={correctCount}
        totalCount={totalAlerts}
        wrongItems={wrongItems}
        onComplete={onComplete}
        onScoreUpdate={onScoreUpdate}
        onRetry={handleRetry}
        isReplay={isReplay}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3 h-full min-h-0">
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}

      {/* Progress bar */}
      <div className="flex items-center justify-between text-xs text-slate-400 px-1">
        <span className="font-mono uppercase tracking-wider text-[10px]">Anomaly Triage — Beginner | Classify Alerts</span>
        <div className="flex items-center gap-3">
          <span className="font-mono">{classifiedCount} / {totalAlerts} classified</span>
          <button
            onClick={() => setShowHelp(true)}
            title="How to play"
            className="w-5 h-5 rounded-full border border-indigo-700/60 bg-indigo-950/60 text-indigo-400 hover:text-indigo-200 hover:border-indigo-500 text-[11px] font-bold flex items-center justify-center transition-colors"
          >
            ?
          </button>
        </div>
      </div>
      <div className="w-full h-1 bg-slate-800 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-indigo-500 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: totalAlerts > 0 ? `${(classifiedCount / totalAlerts) * 100}%` : '0%' }}
          transition={{ type: 'spring', stiffness: 120, damping: 20 }}
        />
      </div>

      {/* Main split panels */}
      <div className="flex gap-3 flex-1 min-h-0 overflow-hidden">
        {/* Left: alert feed */}
        <div className="w-[220px] shrink-0 flex flex-col gap-2 overflow-y-auto pr-1">
          <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest px-1 mb-1">Alert Feed</div>
          {alerts.map((alert) => (
            <AlertRow
              key={alert.id}
              alert={alert}
              isSelected={selectedId === alert.id}
              classification={classifications[alert.id]}
              onClick={() => setSelectedId(alert.id)}
            />
          ))}
        </div>

        {/* Right: device detail */}
        <motion.div
          key={shakeTrigger}
          animate={shakeTrigger > 0 ? shake : {}}
          className="flex-1 min-w-0 bg-slate-900/50 rounded-xl border border-slate-700/40 p-4 overflow-y-auto"
        >
          <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-3">Device Detail</div>
          <DetailPanel alert={selected} />
        </motion.div>
      </div>

      {/* Feedback */}
      <FeedbackToast feedback={feedback} onDismiss={() => setFeedback(null)} />

      {/* Bottom bar: classification buttons + score */}
      <div className="flex items-center justify-between gap-3 pt-1">
        <div className="flex gap-2">
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            disabled={!selected || isClassified}
            onClick={() => classify('threat')}
            className={[
              'px-4 py-2 rounded-lg text-sm font-semibold border transition-colors',
              !selected || isClassified
                ? 'opacity-40 cursor-not-allowed bg-slate-800 border-slate-700 text-slate-500'
                : 'bg-red-950/60 border-red-700/60 text-red-300 hover:bg-red-900/60 hover:border-red-600',
            ].join(' ')}
          >
            FLAG AS THREAT
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            disabled={!selected || isClassified}
            onClick={() => classify('clean')}
            className={[
              'px-4 py-2 rounded-lg text-sm font-semibold border transition-colors',
              !selected || isClassified
                ? 'opacity-40 cursor-not-allowed bg-slate-800 border-slate-700 text-slate-500'
                : 'bg-green-950/60 border-green-700/60 text-green-300 hover:bg-green-900/60 hover:border-green-600',
            ].join(' ')}
          >
            MARK CLEAN
          </motion.button>
        </div>

        {/* Defense score */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-indigo-800/50 bg-indigo-950/40">
          <span className="text-[10px] text-indigo-400 font-mono uppercase tracking-wider">Defense Score</span>
          <span className="text-sm font-bold text-indigo-200 font-mono">{score} pts</span>
        </div>
      </div>
    </div>
  );
}

export default AnomalyTriage;
